// Shared source adapters for browser direct requests and the optional Node gateway.
const annualUrl = year => `https://history.macaumarksix.com/history/macaujc2/y/${year}`;
// TYC-compatible number mapping, checked against all 49 numbers on 2026-10-13.
// shortcut: fixed display convention; update this table if the chosen convention changes.
const ELEMENT_GROUPS = {
  金: [4,5,12,13,26,27,34,35,42,43],
  木: [8,9,16,17,24,25,38,39,46,47],
  水: [1,14,15,22,23,30,31,44,45],
  火: [2,3,10,11,18,19,32,33,40,41,48,49],
  土: [6,7,20,21,28,29,36,37],
};
const elements = Object.fromEntries(Object.entries(ELEMENT_GROUPS).flatMap(([element, numbers]) => numbers.map(n => [n, element])));
const code = value => String(value).split(',').map(Number).join(',');
function numbersOf(row) {
  return typeof row?.openCode === 'string' ? row.openCode.split(',').map(x => x.trim() ? Number(x) : NaN) : [];
}
function isComplete(row) {
  const numbers = numbersOf(row);
  return numbers.length === 7 && numbers.every(n => Number.isInteger(n) && n >= 1 && n <= 49);
}
function adapt(row) {
  const numbers = numbersOf(row);
  if (!/^\d{7}$/.test(String(row?.expect)) || Number(String(row.expect).slice(4)) < 1) throw new Error('澳门期号格式异常');
  if (!isComplete(row) || new Set(numbers).size !== 7) throw new Error('澳门开奖号码不完整或重复');
  const date = row.openTime;
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date) || !Number.isFinite(Date.parse(date.replace(' ', 'T') + '+08:00'))) throw new Error('澳门开奖时间格式异常');
  if (String(row.expect).slice(0, 4) !== date.slice(0, 4)) throw new Error('澳门期号与日期年份不一致');
  return { preDrawIssue: String(row.expect), preDrawCode: numbers.join(','), preDrawTime: date, chineseZodiac: typeof row.zodiac === 'string' ? row.zodiac.split(',') : [], fiveElements: numbers.map(n => elements[n]) };
}
function mergeAnnual(history, rows, year) {
  if (!Array.isArray(rows)) throw new Error('澳门年度数据格式异常');
  const byIssue = new Map(history.map(row => [String(row.preDrawIssue), row]));
  for (const row of rows) {
    if (!/^\d{7}$/.test(String(row?.expect))) throw new Error('澳门期号格式异常');
    // The current-year endpoint returns a rolling year, including last year's draws.
    if (Number(String(row.expect).slice(0, 4)) !== year) continue;
    const adapted = adapt(row);
    const previous = byIssue.get(adapted.preDrawIssue);
    if (previous && code(previous.preDrawCode) !== adapted.preDrawCode) throw new Error('澳门同期开奖数据冲突');
    byIssue.set(adapted.preDrawIssue, adapted);
  }
  return [...byIssue.values()].sort((a, b) => Number(b.preDrawIssue) - Number(a.preDrawIssue));
}
async function readAnnual(request, year) {
  const response = await request(annualUrl(year));
  if (response?.code !== 200 || response.result !== true || !Array.isArray(response.data)) throw new Error('澳门年度数据暂时不可用');
  return mergeAnnual([], response.data, year);
}
async function loadMacau(request, year) {
  const currentYear = Number(new Intl.DateTimeFormat('en', { timeZone: 'Asia/Macau', year: 'numeric' }).format(new Date()));
  const [live, history, currentHistory] = await Promise.all([
    request(`https://macaumarksix.com/api/live2?time=${Date.now()}`),
    readAnnual(request, year),
    year === currentYear ? Promise.resolve(null) : readAnnual(request, currentYear),
  ]);
  const liveRows = Array.isArray(live) ? live : live && typeof live === 'object' && live.expect ? [live] : null;
  if (!liveRows?.length) throw new Error('澳门最新开奖数据格式异常');
  // live2 reveals balls one by one: keep the latest completed draw during that interval.
  const completed = liveRows.filter(isComplete).map(adapt);
  const candidates = [...completed, ...(currentHistory || history)];
  const byIssue = new Map();
  for (const row of candidates) {
    const previous = byIssue.get(row.preDrawIssue);
    if (previous && previous.preDrawCode !== row.preDrawCode) throw new Error('澳门最新开奖与历史数据冲突');
    byIssue.set(row.preDrawIssue, row);
  }
  const latest = [...byIssue.values()].sort((a, b) => Number(b.preDrawIssue) - Number(a.preDrawIssue))[0];
  if (!latest) throw new Error('澳门暂未提供完整开奖结果');
  if (Number(latest.preDrawIssue.slice(0, 4)) === year && !history.some(row => row.preDrawIssue === latest.preDrawIssue)) history.unshift(latest);
  const latestYear = Number(latest.preDrawIssue.slice(0, 4));
  const expected = year < latestYear ? (Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1)) / 86400000 : year === latestYear ? Number(latest.preDrawIssue.slice(4)) : 0;
  if (history.length !== expected || history.some((row, i) => Number(row.preDrawIssue) !== year * 1000 + expected - i)) throw new Error('澳门年度开奖记录缺期');
  // Match the source website’s 21:25 cutoff; result timestamps remain unchanged.
  const nextDate = new Date(Date.parse(latest.preDrawTime.slice(0, 10) + 'T00:00:00Z') + 86400000).toISOString().slice(0, 10);
  return {
    latest: { ...latest, drawIssue: nextDate.slice(0, 4) !== latest.preDrawIssue.slice(0, 4) ? `${nextDate.slice(0, 4)}001` : String(Number(latest.preDrawIssue) + 1), drawTime: `${nextDate} 21:25:00` },
    history, historyAvailable: true, nextTimeEstimated: true, fetchedAt: new Date().toISOString(),
  };
}
const loadMacauYear = loadMacau;

async function loadLotterySource(request, game, year) {
  if (game === 'hk') {
    const [latest, history, elements] = await Promise.all([
      request('https://1680660.com/smallSix/findSmallSixInfo.do'),
      request(`https://1680660.com/smallSix/findSmallSixHistory.do?year=${year}&type=1`),
      request(`https://1680660.com/smallSix/findSmallSixHistory.do?year=${year}&type=2`).catch(() => null),
    ]);
    if (latest.errorCode !== 0 || history.errorCode !== 0 || latest.result?.businessCode !== 0 || history.result?.businessCode !== 0) throw new Error('Upstream error');
    const elementRows = elements?.errorCode === 0 && elements.result?.businessCode === 0
      ? elements.result.data?.bodyList || [] : [];
    const byIssue = new Map(elementRows.map(row => [String(row.issue), row]));
    const rows = history.result.data.bodyList.map(row => {
      const match = byIssue.get(String(row.issue));
      return {...row, fiveElements: match?.preDrawCode === row.preDrawCode ? match.czAndFe : []};
    });
    return { latest: latest.result.data, history: rows, fetchedAt: new Date().toISOString() };
  }
  return loadMacauYear(request, year);
}

module.exports = { loadLotterySource, loadMacau, loadMacauYear, mergeAnnual };
